import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule, MatTable, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import * as XLSX from 'xlsx';

import { LoggerService } from '../../../../core/services/logger.service';
import { LiquidacionesService } from '../../../../services/liquidaciones.service';
import { LiquidacionesNavigationService } from '../../../../services/liquidaciones-navigation.service';

type LiquidacionVdDetalle = any;

interface ContenedorVd {
    nroContenedor: string;
    nave: string;
    etd: Date;
    vd: number;
}

@Component({
    selector: 'app-liquidacion-vd-detalle',
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatButtonModule,
        MatIconModule,
        MatTableModule,
        MatSortModule,
        MatSnackBarModule,
        MatTabsModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        MatPaginatorModule,
    ],
    templateUrl: './liquidacion-vd-detalle.component.html',
    styleUrl: './liquidacion-vd-detalle.component.scss',
})
export class LiquidacionVdDetalleComponent implements OnInit {
    // ID de la liquidación
    liquidacionId: number = 0;
    // Número de liquidación (N° Liq. del listado)
    numeroLiquidacionVD: string | null = null;

    // Datos de la liquidación
    liquidacion: LiquidacionVdDetalle | null = null;

    // Datos de contenedores VD desde excel-data
    contenedores: any[] = [];
    contenedoresDataSource = new MatTableDataSource<any>([]);
    totalesContenedores: any = null;
    isLoadingContenedores = false;
    
    // Columnas de la tabla de contenedores VD
    contenedoresColumns: string[] = [
        'trafico',
        'exportador',
        'naviera',
        'nave',
        'viaje',
        'fechaEmbarque',
        'embarque',
        'descarga',
        'grupoEspecie',
        'especie',
        'booking',
        'bl',
        'contenedor',
        'cantidad',
        'tarifaPublica',
        'tarifaNeta',
        'vdUsd',
        'tc',
        'vdClp'
    ];

    // Estado de carga
    isLoading: boolean = false;
    loadingMessage: string = 'Cargando detalle de liquidación VD...';
    error: string | null = null;

    // Tabs
    selectedTabIndex: number = 0;

    @ViewChild(MatSort) sort!: MatSort;
    @ViewChild(MatTable) table!: MatTable<ContenedorVd>;
    @ViewChild(MatPaginator) contenedoresPaginator!: MatPaginator;
    @ViewChild('contenedoresSort') contenedoresSort!: MatSort;

    constructor(
        // eslint-disable-next-line no-unused-vars
        private route: ActivatedRoute,
        // eslint-disable-next-line no-unused-vars
        private router: Router,
        // eslint-disable-next-line no-unused-vars
        private liquidacionesService: LiquidacionesService,
        // eslint-disable-next-line no-unused-vars
        private snackBar: MatSnackBar,
        // eslint-disable-next-line no-unused-vars
        private logger: LoggerService,
        // eslint-disable-next-line no-unused-vars
        private navigationService: LiquidacionesNavigationService
    ) {}

    ngOnInit(): void {
        // Obtener ID de la liquidación de la URL
        const id = this.route.snapshot.paramMap.get('id');
        // Obtener N° Liq. enviado desde el listado (Router state o query param)
        const state: any = history.state || {};
        this.numeroLiquidacionVD = state?.numeroLiquidacion || this.route.snapshot.queryParamMap.get('nl');
        if (id) {
            this.liquidacionId = parseInt(id);
            this.cargarDatos();
        } else {
            this.snackBar.open('ID de liquidación no válido', 'Cerrar', {
                duration: 5000,
                panelClass: ['error-snackbar'],
            });
            this.volver();
        }
    }

    cargarDatos(): void {
        this.isLoading = true;
        this.loadingMessage = 'Cargando detalle de liquidación VD...';
        this.logger.info(
            `🔄 Cargando detalle de liquidación VD: ${this.liquidacionId}`,
            'LiquidacionVdDetalleComponent'
        );

        this.liquidacionesService
            .getLiquidacionVdById(this.liquidacionId)
            .pipe(
                catchError((error) => {
                    this.logger.error(
                        'Error al cargar detalle de liquidación VD',
                        error as Error,
                        'LiquidacionVdDetalleComponent'
                    );
                    this.error = 'Error al cargar los datos de la liquidación';
                    this.snackBar.open(
                        'Error al cargar detalle de liquidación',
                        'Cerrar',
                        {
                            duration: 5000,
                            panelClass: ['error-snackbar'],
                        }
                    );
                    return of(null);
                }),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe((response: any) => {
                // El endpoint de VD retorna directamente el payload plano del embarque
                // Si viene envuelto (success/data), considerar ambos escenarios
                const data = response?.success && response.data ? response.data : response;
                if (data && (data.clienteId || data.cliente)) {
                    this.liquidacion = data;
                    this.logger.info(
                        `✅ Detalle de liquidación VD cargado: ${this.liquidacionId}`,
                        'LiquidacionVdDetalleComponent'
                    );
                    // Cargar contenedores después de cargar la liquidación
                    this.cargarContenedores();
                } else {
                    this.error = 'No se encontró la liquidación solicitada';
                    this.snackBar.open(
                        'No se encontró la liquidación solicitada',
                        'Cerrar',
                        {
                            duration: 5000,
                            panelClass: ['warning-snackbar'],
                        }
                    );
                }
            });
    }

    cargarContenedores(): void {
        if (!this.liquidacionId) return;
        
        this.isLoadingContenedores = true;
        const endpointId = this.numeroLiquidacionVD ? parseInt(this.numeroLiquidacionVD, 10) : this.liquidacionId;
        
        this.logger.info(
            `🔄 Cargando contenedores para liquidación VD: ${endpointId}`,
            'LiquidacionVdDetalleComponent'
        );

        this.liquidacionesService
            .getLiquidacionVdExcelData(endpointId)
            .pipe(
                catchError((error) => {
                    this.logger.error(
                        'Error al cargar contenedores',
                        error as Error,
                        'LiquidacionVdDetalleComponent'
                    );
                    this.snackBar.open(
                        'Error al cargar contenedores',
                        'Cerrar',
                        {
                            duration: 5000,
                            panelClass: ['error-snackbar'],
                        }
                    );
                    return of(null);
                }),
                finalize(() => {
                    this.isLoadingContenedores = false;
                })
            )
            .subscribe((response: any) => {
                if (response?.success && response.data) {
                    this.contenedores = response.data.rows || [];
                    this.totalesContenedores = response.data.totals || null;
                    this.contenedoresDataSource.data = this.contenedores;
                    
                    // Conectar paginator y sort después de que la vista se actualice
                    setTimeout(() => {
                        if (this.contenedoresPaginator) {
                            this.contenedoresDataSource.paginator = this.contenedoresPaginator;
                        }
                        if (this.contenedoresSort) {
                            this.contenedoresDataSource.sort = this.contenedoresSort;
                        }
                    }, 0);
                    
                    this.logger.info(
                        `✅ Contenedores cargados: ${this.contenedores.length}`,
                        'LiquidacionVdDetalleComponent'
                    );
                } else {
                    this.contenedores = [];
                    this.contenedoresDataSource.data = [];
                    this.logger.warn(
                        'No se encontraron contenedores en la respuesta',
                        'LiquidacionVdDetalleComponent'
                    );
                }
            });
    }

    volver(): void {
        // Volver al listado manteniendo el estado (filtros, paginación, etc.)
        this.navigationService.backToVdList();
    }

    generarExcel(): void {
        if (!this.liquidacion) return;
        this.isLoading = true;
        this.loadingMessage = 'Generando Excel...';
        this.logger.info(
            `📊 Generando Excel para liquidación VD: ${this.liquidacionId}`,
            'LiquidacionVdDetalleComponent'
        );
        const endpointId = this.numeroLiquidacionVD ? parseInt(this.numeroLiquidacionVD, 10) : this.liquidacionId;
        this.liquidacionesService
            .getLiquidacionVdExcelData(endpointId)
            .pipe(
                finalize(() => (this.isLoading = false)),
                catchError((error) => {
                    this.logger.error(
                        'Error al obtener datos para Excel',
                        error as Error,
                        'LiquidacionVdDetalleComponent'
                    );
                    this.snackBar.open(
                        'Error al generar Excel',
                        'Cerrar',
                        {
                            duration: 5000,
                            panelClass: ['error-snackbar'],
                        }
                    );
                    return of(null);
                })
            )
            .subscribe((response) => {
                if (response?.success && response.data) {
                    const fileNum = this.numeroLiquidacionVD || String(this.liquidacionId);
                    this.crearYDescargarExcel(
                        response.data,
                        `liquidacion_vd_${fileNum}.xlsx`
                    );
                } else {
                    this.snackBar.open(
                        'No se pudieron obtener los datos para el Excel',
                        'Cerrar',
                        {
                            duration: 5000,
                            panelClass: ['error-snackbar'],
                        }
                    );
                }
            });
    }

    private crearYDescargarExcel(data: any, nombreArchivo: string): void {
        try {
            const wb = XLSX.utils.book_new();

            // Detalle: estructura VD -> data.rows (array) o data.detalle.rows
            const detalleRows = Array.isArray(data?.rows)
                ? data.rows
                : (Array.isArray(data?.detalle?.rows) ? data.detalle.rows : []);
            if (detalleRows.length > 0) {
                const wsDetalle = XLSX.utils.json_to_sheet(detalleRows);
                XLSX.utils.book_append_sheet(wb, wsDetalle, 'Detalle');
            }

            // Totales: objeto (data.totals) o array (data.totales.rows)
            const totalesArray = Array.isArray(data?.totales?.rows) ? data.totales.rows : null;
            const totalesObj = data?.totales && !Array.isArray(data.totales)
                ? data.totales
                : (data?.totals && !Array.isArray(data.totals) ? data.totals : null);
            if (totalesArray && totalesArray.length > 0) {
                const wsTotales = XLSX.utils.json_to_sheet(totalesArray);
                XLSX.utils.book_append_sheet(wb, wsTotales, 'Totales');
            } else if (totalesObj && Object.keys(totalesObj).length > 0) {
                const wsTotales = XLSX.utils.json_to_sheet([totalesObj]);
                XLSX.utils.book_append_sheet(wb, wsTotales, 'Totales');
            }

            // Fallback: otras llaves con rows
            Object.keys(data || {}).forEach((key) => {
                if (['rows', 'detalle', 'totals', 'totales'].includes(key)) return;
                const value = data[key];
                if (value && Array.isArray(value.rows) && value.rows.length > 0) {
                    const ws = XLSX.utils.json_to_sheet(value.rows);
                    XLSX.utils.book_append_sheet(wb, ws, key.substring(0, 31));
                } else if (Array.isArray(value) && value.length > 0) {
                    const ws = XLSX.utils.json_to_sheet(value);
                    XLSX.utils.book_append_sheet(wb, ws, key.substring(0, 31));
                }
            });

            if (wb.SheetNames.length === 0) {
                this.snackBar.open('No hay datos para generar el archivo Excel.', 'Cerrar', {
                    duration: 3000,
                    panelClass: ['warning-snackbar'],
                });
                return;
            }

            XLSX.writeFile(wb, nombreArchivo);

            this.snackBar.open('Excel generado exitosamente', 'Cerrar', {
                duration: 3000,
                panelClass: ['success-snackbar'],
            });
        } catch (error) {
            this.logger.error(
                'Error al crear archivo Excel',
                error as Error,
                'LiquidacionVdDetalleComponent'
            );
            this.snackBar.open(
                'Error al generar Excel',
                'Cerrar',
                {
                    duration: 5000,
                    panelClass: ['error-snackbar'],
                }
            );
        }
    }

    generarPdf(): void {
        if (!this.liquidacion) return;

        this.isLoading = true;
        this.loadingMessage = 'Generando PDF...';
        this.logger.info(
            `📄 Generando PDF para liquidación VD: ${this.liquidacionId}`,
            'LiquidacionVdDetalleComponent'
        );

        const endpointId = this.numeroLiquidacionVD ? parseInt(this.numeroLiquidacionVD, 10) : this.liquidacionId;
        this.liquidacionesService.getLiquidacionVdPdf(endpointId)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (blob: Blob) => {
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `liquidacion_vd_${this.numeroLiquidacionVD || this.liquidacionId}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);

                    this.snackBar.open('PDF generado exitosamente', 'Cerrar', {
                        duration: 3000,
                        panelClass: ['success-snackbar'],
                    });
                },
                error: (error: any) => {
                    this.logger.error(
                        'Error al generar PDF',
                        error as Error,
                        'LiquidacionVdDetalleComponent'
                    );
                    this.snackBar.open('Error al generar PDF', 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar'],
                    });
                }
            });
    }

    formatearMesAnio(mesAnio: string): string {
        if (!mesAnio) return '';
        
        const [mes, anio] = mesAnio.split('-');
        
        if (!mes || !anio) {
            return mesAnio;
        }
        
        const nombreMes = this.obtenerNombreMes(parseInt(mes));
        return `${nombreMes} ${anio}`;
    }

    obtenerMesAnioDesdeFecha(fecha: string | Date | null | undefined): string {
        if (!fecha) return '';
        try {
            const fechaObj = typeof fecha === 'string' ? new Date(fecha) : fecha;
            if (isNaN(fechaObj.getTime())) return '';
            const mes = fechaObj.getMonth() + 1; // getMonth() retorna 0-11
            const anio = fechaObj.getFullYear();
            const nombreMes = this.obtenerNombreMes(mes);
            return `${nombreMes} ${anio}`;
        } catch {
            return '';
        }
    }

    private obtenerNombreMes(mes: number): string {
        const meses = [
            'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
            'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
        ];
        return meses[mes - 1] || '';
    }

    formatearNumero(valor: number): string {
        if (valor === null || valor === undefined) return '0';
        return valor.toLocaleString('es-CL');
    }

    formatearMoneda(valor: number): string {
        if (valor === null || valor === undefined) return '$ 0';
        return `$ ${valor.toLocaleString('es-CL')}`;
    }

    formatearFechaCorta(fecha: string | Date | null | undefined): string {
        if (!fecha) return 'N/D';
        try {
            const fechaObj = typeof fecha === 'string' ? new Date(fecha) : fecha;
            if (isNaN(fechaObj.getTime())) return 'N/D';
            return fechaObj.toLocaleDateString('es-CL', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });
        } catch {
            return 'N/D';
        }
    }

    formatearMonedaUsd(valor: number): string {
        if (valor === null || valor === undefined) return 'USD $ 0';
        return `USD $ ${valor.toLocaleString('es-CL')}`;
    }

    formatearFecha(fecha: string | Date | null | undefined): string {
        if (!fecha) {
            return 'N/A';
        }
        try {
            return new Date(fecha).toLocaleDateString('es-CL', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch (error) {
            return 'Fecha inválida';
        }
    }

    getEstadoColor(estado: string | null | undefined): string {
        if (!estado || estado.trim() === '') {
            return 'estado-primario';
        }
        const estadoLower = estado.toLowerCase().trim();
        switch (estadoLower) {
            case 'liquidado':
                return 'estado-azul';
            case 'pendiente':
            case 'en proceso':
                return 'estado-naranja';
            case 'pagado':
                return 'estado-verde';
            case 'rechazado':
            case 'cancelado':
            case 'anulado':
                return 'estado-rojo';
            case 'cerrado':
                return 'estado-gris';
            default:
                return 'estado-primario';
        }
    }

    onTabIndexChange(index: number): void {
        this.selectedTabIndex = index;
        this.logger.info(
            `Tab cambiado a índice: ${index}`,
            'LiquidacionVdDetalleComponent'
        );
    }
}
